/**
 * Org-level display currency, and every number formatter the app is allowed to use.
 *
 * The backend stores every monetary value as a raw number in the org's own currency
 * (`organizations.currency`, ISO 4217) — nothing ever converts — so formatting is the ONLY
 * place a currency code matters on the frontend. And the currency decides more than the
 * symbol: it decides where the commas go, for money and for plain counts alike. An INR org
 * reads 16,70,000 pieces and ₹31,28,96,850.00; an AED org reads 1,670,000 and AED 312,896,850.00.
 * Neither the viewer's browser locale nor a per-screen choice gets a say - see
 * `@diagon-alley/common`'s numberFormat, which both this and the backend's documents call.
 *
 * `setDisplayCurrency` is called once per session when the org config loads (the brand shell
 * and the vendor portal each do it); every formatter then reads the module-level code so plain
 * functions (grid cells, config objects, column definitions) can format without a hook.
 */
import {
    currencySymbolFor,
    DEFAULT_CURRENCY,
    formatCount as formatCountFor,
    formatMoneyFor,
    localeForCurrency,
    type CountFormatOptions,
} from '@diagon-alley/common';

let displayCurrency = DEFAULT_CURRENCY;

export const setDisplayCurrency = (code: string | undefined | null): void => {
    if (code) displayCurrency = code.trim().toUpperCase();
};

export const getDisplayCurrency = (): string => displayCurrency;

/** The locale the org's figures group in - for the rare caller that needs Intl directly. */
export const displayLocale = (): string => localeForCurrency(displayCurrency);

/** The symbol (or prefix) for the org's currency — '₹', 'AED ', '$', …. */
export const currencySymbol = (code = displayCurrency): string => currencySymbolFor(code);

/**
 * `1234567.8` → `₹12,34,567.80` / `AED 1,234,567.80` — org symbol, org grouping, two decimals.
 *
 * Pass `{ decimals }` where a rounded figure is the point (a KPI tile, a chart label).
 */
export const formatMoney = (
    value: number | string | null | undefined,
    code: string | null | undefined = displayCurrency,
    options?: CountFormatOptions,
): string => formatMoneyFor(value, code ?? displayCurrency, options);

/**
 * A plain figure - a quantity, a count - grouped the org's way: `16,70,000` for INR.
 *
 * The one replacement for `n.toLocaleString()`. Without options the fraction behaves exactly
 * as `toLocaleString()` did (up to three decimals, trailing zeros dropped), so moving a call
 * here changes only its grouping. `{ decimals: 2 }` fixes the fraction; `{ maxDecimals: 2 }`
 * caps it.
 */
export const formatCount = (
    value: number | string | null | undefined,
    options?: CountFormatOptions,
): string => formatCountFor(value, displayCurrency, options);

/**
 * A figure squeezed for an axis or a tile - `12L` / `1.2Cr` for an INR org, `1.2M` for AED.
 *
 * Compact notation is locale-defined, so the same rule gives an Indian org its lakhs and
 * crores and everyone else their K/M/B without a second table here.
 */
export const formatCompact = (value: number, maxDecimals = 1): string =>
    new Intl.NumberFormat(displayLocale(), {
        notation: 'compact',
        maximumFractionDigits: maxDecimals,
    }).format(value);
